import { useCallback } from 'react';
import { isPasswordProtected } from '@design-stack-ct/assets-sdk';
import { OnBeforeUploadCallback, OnUploadStartCallback } from '@design-stack-vista/upload-components';
import { VistaAsset } from '@design-stack-vista/vista-assets-sdk';
import { ValidationState, ValidatorType } from '@vp/upload-and-print-validator';
import { MAX_ALLOWED_CLIENT_VALIDATION_FILE_SIZE_BYTES, useClientValidation } from 'src/clientValidation';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useProcessUpload, useUploadContext } from 'src/hooks/UploadContextHooks';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { usePromptContext } from 'src/providers/PromptContextProvider';
import { Upload } from 'src/services/UploadAndPrintService';
import { UN_EMBEDDED_FONTS_PAGE_LIMIT, UPLOAD_ERRORS_LOCALE_KEY_MAP } from 'src/utils/Constants';
import { validateUnEmbeddedFonts } from 'src/utils/getUnEmbeddedFonts';
import { noticeError } from 'src/utils/newRelicUtil';
import { getValidationsForProduct, renameFile } from 'src/utils/validationUtil';

export enum FileValidationErrorType {
  PasswordProtected = 'PasswordProtected',
  UnEmbeddedFonts = 'UnEmbeddedFonts',
  Unknown = 'UNKNOWN',
}

const FILE_UPLOAD_ERROR = 'An error occurred during file upload';

export function useFileUpload() {
  const {
    setUpload,
    setIsUploading,
    setUploadRestrictionFailed,
    setFileSize,
    setCurrentUploads,
    currentUploads,
  } = useUploadContext();
  const { setValidations, validationManager, fixedPageSizeWarning } = useValidationContext();
  const { productInformation, setProductInformation } = useProductContext();
  const { setError } = useErrorContext();
  const { processUpload } = useProcessUpload();
  const {
    setShowProductOptionsPrompt,
    setShowSpecsAndTemplatePrompt,
    setShowValidationErrorPrompt,
  } = usePromptContext();
  const { isClientValidationEnabled, setClientValidation } = useClientValidation();

  const handleBeforeUpload: OnBeforeUploadCallback = useCallback(files => {
    if (files.length === 0) {
      const error = new Error('No files selected for upload');
      noticeError(error, { method: 'handleBeforeUpload', files });
      return;
    }
    // Replacing special characters in the filename with an underscore to validate the filename before creating the asset in Sherbert.
    const validFileName = files[0].name.replace(/[^\w.-]/g, '_');
    files[0] = renameFile(files[0], validFileName);
  }, []);

  const addAsset = useCallback(
    async (file: File, upload: Promise<void | VistaAsset>, signal: AbortSignal) => {
      const shouldUseClientValidation = Boolean(
        file.size < MAX_ALLOWED_CLIENT_VALIDATION_FILE_SIZE_BYTES && isClientValidationEnabled && validationManager
      );
      if (shouldUseClientValidation && validationManager) {
        await validationManager.setFile(file);
        validationManager.runValidation();
      } else {
        // These are the file checks that are done on the client side
        // before the file gets uploaded to the server.

        const isPasswordProtectedPdf = await isPasswordProtected(file);
        if (isPasswordProtectedPdf.PasswordProtected) {
          throw new Error(
            JSON.stringify({
              type: FileValidationErrorType.PasswordProtected,
              message: 'File processing failed, file is password protected.',
            })
          );
        }

        const unEmbeddedFontPages = await validateUnEmbeddedFonts(file);

        if (unEmbeddedFontPages.length > 0) {
          throw new Error(
            JSON.stringify({
              type: FileValidationErrorType.UnEmbeddedFonts,
              message:
                unEmbeddedFontPages.length < UN_EMBEDDED_FONTS_PAGE_LIMIT
                  ? 'Your file has non-embedded fonts in pages.'
                  : 'Your file has non-embedded fonts.',
              pages: unEmbeddedFontPages,
              bodyDescription: 'Please embed all fonts in your file before uploading.',
            })
          );
        }
      }
      setClientValidation(shouldUseClientValidation);
      const asset = await upload;
      if (!asset) {
        throw new Error('asset is undefined');
      }
      // If the upload was aborted, don't perform any actions on the asset
      if (signal.aborted) {
        return;
      }

      await asset.prepare();
      const newUpload: Upload = {
        fileUri: asset.getUrl(),
        uploadedFileName: asset.data?.info.storage?.fileName,
        thumbnailUrl: asset.thumbnail.getUrl(),
      };
      setUpload(newUpload);
      setIsUploading(false);
      await processUpload(newUpload, shouldUseClientValidation);
    },
    [isClientValidationEnabled, validationManager, setClientValidation, setUpload, setIsUploading, processUpload]
  );

  const handleUploadStart: OnUploadStartCallback = useCallback(
    async results => {
      fixedPageSizeWarning(null);
      setCurrentUploads(results);
      // If results is empty, something went wrong with the upload
      if (results.length < 1) {
        setIsUploading(false);
        return;
      }
      // Only one file can be uploaded at a time
      const { file, upload, signal } = results[0];
      setFileSize(file.size);

      try {
        await addAsset(file, upload, signal);
      } catch (error) {
        // If the upload was aborted, don't set any error state
        if (signal.aborted) {
          return;
        }

        if (error instanceof Error) {
          let errorMessage = '';
          let errorType = '';
          let pagesData: number[] = [];
          let modalBodyDescription = '';

          try {
            const errorData = JSON.parse(error.message);
            errorMessage = errorData?.message || error.message;
            errorMessage = errorMessage && errorMessage !== '{}' ? errorMessage : FILE_UPLOAD_ERROR; // handle empty error
            errorType = errorData?.type || FileValidationErrorType.Unknown;
            pagesData = errorData?.pages || [];
            modalBodyDescription = errorData?.bodyDescription || '';
          } catch (parseError) {
            errorMessage = `Failed to parse error message. Original error: ${error.message}`;
          }

          const validations = await getValidationsForProduct(productInformation);

          if (errorType === FileValidationErrorType.UnEmbeddedFonts) {
            validations.push({
              id: ValidatorType.UnEmbeddedFontsValidator,
              title: `validations.${ValidatorType.UnEmbeddedFontsValidator}.title`,
              message:
                UPLOAD_ERRORS_LOCALE_KEY_MAP[errorMessage as keyof typeof UPLOAD_ERRORS_LOCALE_KEY_MAP] || errorMessage,
              state: ValidationState.Error,
              details: {
                pagesData,
                bodyDescription:
                  UPLOAD_ERRORS_LOCALE_KEY_MAP[modalBodyDescription as keyof typeof UPLOAD_ERRORS_LOCALE_KEY_MAP] ||
                  modalBodyDescription,
              },
              tokenValues: {},
            });
          }

          if (errorType === FileValidationErrorType.PasswordProtected) {
            validations.push({
              id: ValidatorType.PasswordValidator,
              title: `validations.${ValidatorType.PasswordValidator}.title`,
              message: `validations.${ValidatorType.PasswordValidator}.message`,
              state: ValidationState.Error,
              details: {},
              tokenValues: {},
            });
          }

          setValidations(validations);
          setIsUploading(false);
          const uploadError = new Error(errorMessage);

          noticeError(uploadError, {
            method: 'onUploadStart',
            errorType,
            errorPages: pagesData,
            error: error,
          });
          return;
        }
        const uploadError = new Error(FILE_UPLOAD_ERROR);
        setError(uploadError);
        noticeError(uploadError, {
          method: 'onUploadStart',
        });
        setIsUploading(false);
      }
    },
    [
      fixedPageSizeWarning,
      setCurrentUploads,
      setFileSize,
      setIsUploading,
      addAsset,
      setError,
      productInformation,
      setValidations,
    ]
  );

  const resetUploader = useCallback(() => {
    setUpload(null);
    setValidations(null);
    setIsUploading(false);
    setUploadRestrictionFailed(false);
    setFileSize(0);
    currentUploads.forEach(({ abort }) => abort());
    setCurrentUploads([]);
    setShowProductOptionsPrompt(false);
    setShowSpecsAndTemplatePrompt(false);
    setShowValidationErrorPrompt(false);
    setProductInformation({ ...productInformation, recommendedProductData: undefined });
    if (validationManager) {
      validationManager.setFile(null);
    }
    fixedPageSizeWarning(null);
  }, [
    currentUploads,
    fixedPageSizeWarning,
    productInformation,
    setCurrentUploads,
    setFileSize,
    setIsUploading,
    setProductInformation,
    setShowProductOptionsPrompt,
    setShowSpecsAndTemplatePrompt,
    setShowValidationErrorPrompt,
    setUpload,
    setUploadRestrictionFailed,
    setValidations,
    validationManager,
  ]);

  return { handleUploadStart, resetUploader, handleBeforeUpload };
}
